/*
 * Unfinished personalised boxes.
 *
 * The box builder saves the shopper's picks here as they go, so a box that is
 * started but not yet added to the cart survives a trip to a product page.
 * `<box-reminder>` (snippets/box-reminder.liquid) reads them back on every
 * other page and offers a way back to the box.
 *
 * Drafts live in localStorage, keyed by the box variant id, and expire after
 * a week without changes.
 */
(function () {
  if (window.BoxDraft) return;

  const STORAGE_KEY = 'humma:box-drafts';
  const DISMISS_KEY = 'humma:box-reminder-dismissed';
  const MAX_AGE = 7 * 24 * 60 * 60 * 1000;

  function readAll() {
    let drafts;
    try {
      drafts = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }

    const now = Date.now();
    let pruned = false;
    Object.keys(drafts).forEach((id) => {
      const draft = drafts[id];
      if (!draft || !Array.isArray(draft.items) || !draft.items.length || now - draft.updatedAt > MAX_AGE) {
        delete drafts[id];
        pruned = true;
      }
    });
    if (pruned) writeAll(drafts);
    return drafts;
  }

  function writeAll(drafts) {
    try {
      if (Object.keys(drafts).length) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      // Private mode or a full quota: the box simply isn't remembered.
    }
  }

  window.BoxDraft = {
    get(boxId) {
      return readAll()[boxId] || null;
    },

    /** `draft`: { url, title, min, max, items: [{ variantId, quantity, title, image }] } */
    save(boxId, draft) {
      const drafts = readAll();
      if (!draft || !draft.items || !draft.items.length) {
        delete drafts[boxId];
      } else {
        drafts[boxId] = Object.assign({}, draft, { updatedAt: Date.now() });
      }
      writeAll(drafts);
    },

    clear(boxId) {
      this.save(boxId, null);
    },

    /** The draft the shopper touched last, or null. */
    latest() {
      const drafts = readAll();
      return Object.keys(drafts)
        .map((id) => Object.assign({ id }, drafts[id]))
        .sort((a, b) => b.updatedAt - a.updatedAt)[0] || null;
    },
  };

  class BoxReminder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.titleEl = this.querySelector('[data-box-reminder-title]');
      this.hintEl = this.querySelector('[data-box-reminder-hint]');
      this.thumbsEl = this.querySelector('[data-box-reminder-thumbs]');
      this.link = this.querySelector('[data-box-reminder-link]');
      this.closeButton = this.querySelector('[data-box-reminder-close]');

      this.closeButton.addEventListener('click', () => this.dismiss());

      // Keep the page's last lines and the WhatsApp button clear of the bar.
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => this.syncOffset()).observe(this);
      }

      // Another tab finished the box, or Back restored this page from cache.
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY) this.update();
      });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.update();
      });

      this.update();
    }

    update() {
      const draft = window.BoxDraft.latest();
      // The builder has its own bar; never stack a second one on top of it.
      const onBuilder = Boolean(document.querySelector('bundle-box'));

      if (!draft || onBuilder || this.isDismissed(draft)) {
        this.toggle(false);
        return;
      }

      const count = draft.items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
      const missing = Math.max((draft.min || 0) - count, 0);
      const fill = (template) =>
        (template || '')
          .replace('[count]', count)
          .replace('[remaining]', missing)
          .replace('[min]', draft.min || '')
          .replace('[max]', draft.max || '')
          .replace('[box]', draft.title || '');

      this.titleEl.textContent = fill(count === 1 ? this.dataset.textOne : this.dataset.textOther);
      this.hintEl.textContent = fill(missing > 0 ? this.dataset.textRemaining : this.dataset.textReady);
      this.link.href = draft.url;

      this.thumbsEl.innerHTML = '';
      draft.items
        .filter((item) => item.image)
        .slice(0, 4)
        .forEach((item) => {
          const li = document.createElement('li');
          li.className = 'box-reminder__thumb';
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = item.title || '';
          img.width = 40;
          img.height = 50;
          img.loading = 'lazy';
          li.appendChild(img);
          this.thumbsEl.appendChild(li);
        });
      this.thumbsEl.hidden = !this.thumbsEl.children.length;

      this.toggle(true);
    }

    toggle(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.link.tabIndex = visible ? 0 : -1;
      this.closeButton.tabIndex = visible ? 0 : -1;
      document.body.classList.toggle('has-box-reminder', visible);
      this.syncOffset();
    }

    syncOffset() {
      const height = this.classList.contains('is-visible') ? this.offsetHeight : 0;
      document.body.style.setProperty('--box-reminder-height', `${height}px`);
    }

    /* Closing hides the bar for this visit only; the box itself is kept, and
       the bar comes back as soon as the box changes again. */
    dismiss() {
      const draft = window.BoxDraft.latest();
      try {
        window.sessionStorage.setItem(DISMISS_KEY, draft ? `${draft.id}:${draft.updatedAt}` : '');
      } catch (error) {
        // Nothing to remember it in; hide it for this page at least.
      }
      this.toggle(false);
    }

    isDismissed(draft) {
      try {
        return window.sessionStorage.getItem(DISMISS_KEY) === `${draft.id}:${draft.updatedAt}`;
      } catch (error) {
        return false;
      }
    }
  }

  if (!customElements.get('box-reminder')) {
    customElements.define('box-reminder', BoxReminder);
  }
})();
